class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.productsLimit = 8; // Define the products limit
    this.minimumSearchLength = 2; // Minimum search length

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const newSearchTerm = this.getQuery(); // Get current search term
    
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove previous results if not relevant for new term
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm); // Update displayed search term text
    this.searchTerm = newSearchTerm; // Store new search term

    if (!this.searchTerm.length) {
      this.close(true); // Close dropdown if search is empty
      return;
    }

    // Check if search term is less than minimum required length
    if (this.searchTerm.length < this.minimumSearchLength) {
      this.showMinimumCharactersMessage(); // Show message for minimum character requirement
      return; // Exit to prevent further processing
    }

    // Proceed to get search results if the term is valid
    this.getSearchResults(this.searchTerm)
      .then(resultsFound => {
        if (!resultsFound) {
          // Handle case when no results are found
          this.showNoResultsMessage(this.searchTerm);
        }
        // If results are found, they're already rendered by getSearchResults
      });
  }

  showMinimumCharactersMessage() {
    const minimumCharactersHTML = `
      <div class="predictive-search__message">
        <p>Please enter at least ${this.minimumSearchLength} characters to search</p>
      </div>
    `;
    // this.predictiveSearchResults.innerHTML = minimumCharactersHTML;
    this.setAttribute('results', true);
    this.open(); // Open the dropdown to show the message
    this.removeAttribute('loading'); // Ensure loading state is removed
  }

  // Add this method to show the "not found" message
  showNoResultsMessage(searchTerm) {
    const noResultsHTML = `
      <div class="predictive-search__no-results predictive-search__search-for-button">
        <p>No results for ${searchTerm}</p>
      </div>
    `;
    this.predictiveSearchResults.innerHTML = noResultsHTML;
    this.setAttribute('results', true);
    this.open();
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources)
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  // Modified to return a Promise indicating if results were found
  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase(); // Create cache key
    this.setLiveRegionLoadingState(); // Indicate loading for screen readers

    return new Promise((resolve) => {
      if (this.cachedResults[queryKey]) {
        // Use cached results if available
        const hasResults = this.renderSearchResults(this.cachedResults[queryKey]);
        resolve(hasResults);
        return;
      }

      // Fetch search results from server
      fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
        signal: this.abortController.signal, // Allow request cancellation
      })
        .then((response) => {
          if (!response.ok) {
            var error = new Error(response.status);
            this.close();
            throw error;
          }
          return response.text();
        })
        .then((text) => {
          // Process HTML response
          const parsedDOM = new DOMParser().parseFromString(text, 'text/html');
          const resultsSection = parsedDOM.querySelector('#shopify-section-predictive-search');
          
          // Check if there are any results
          const hasResults = this.checkForResults(resultsSection);
          
          if (hasResults) {
            this.limitProducts(resultsSection); // Apply product limit
          }
          
          const resultsMarkup = resultsSection.innerHTML;
          
          // Cache results in all search instances for consistency
          this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          });
          
          if (hasResults) {
            this.renderSearchResults(resultsMarkup);
          }
          
          resolve(hasResults);
        })
        .catch((error) => {
          if (error?.code === 20) {
            // Code 20 means request was aborted intentionally
            resolve(false);
            return;
          }
          this.close();
          throw error;
        });
    });
  }

  // Add method to check if results were found
  checkForResults(resultsSection) {
    // Adjust this selector based on your Shopify theme's structure
    const productItems = resultsSection.querySelectorAll('li.predictive-search__list-item');
    return productItems && productItems.length > 0;
  }

  // New method to limit the number of products in the results
  limitProducts(resultsSection) {
    // Find the products list - adjust the selector based on your Shopify theme's structure
    const productsUl = resultsSection.querySelector('ul.predictive-search__results-list');
    if (!productsUl) return;

    // Get all product items
    const productItems = productsUl.querySelectorAll('li.predictive-search__list-item');
    if (!productItems || productItems.length <= this.productsLimit) return;

    // Remove excess products
    for (let i = this.productsLimit; i < productItems.length; i++) {
      productItems[i].remove();
    }

    // Update the count in the live region if it exists
    const countElement = resultsSection.querySelector('[data-predictive-search-live-region-count-value]');
    if (countElement) {
      const countText = countElement.textContent;
      const regex = /(\d+)/;
      const match = countText.match(regex);
      if (match) {
        const originalCount = parseInt(match[1]);
        const newCount = Math.min(originalCount, this.productsLimit);
        countElement.textContent = countText.replace(regex, newCount);
      }
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  // Updated to return whether results were found
  renderSearchResults(resultsMarkup) {
    // Display results in dropdown
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    // Check if there are results in the markup
    const hasResults = this.predictiveSearchResults.querySelector('li.predictive-search__list-item') !== null;
    
    if (hasResults) {
      this.setLiveRegionResults(); // Update accessibility info
      this.open(); // Show dropdown
    }
    
    return hasResults;
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
